import { ref, computed, watch, onMounted } from 'vue'
import { config } from '@/config.js'
import store from '@/store'
import { useRouter } from '@/composables/common/router/useRouter'
import { useToast } from '@/composables/common/useToast'

export function useInvitacionesAbonados(eventoId) {
    const router = useRouter()
    const toast = useToast()

    // ==================== ESTADO ====================
    const zonas = ref([])
    const search = ref('')
    const errors = ref([])
    const evento = ref(null)
    const loading = ref(false)
    const loadingZonas = ref(false)
    const zonasOriginales = ref([])

    const opcionesTipoInvitacionesAbonados = [
        { value: 1, text: 'En todas las zonas disponibles' },
        { value: 2, text: 'Solo en la misma sección que su abono' },
        { value: 3, text: 'Solo en la misma zona que su abono' }
    ]

    // ==================== COMPUTED ====================
    const esMovil = computed(() => window.innerWidth <= 768)

    const teatro = computed(() => store.getters.getTeatro)

    const reglasRestriccion = computed(() =>
        evento.value?.n_invitaciones_abonados > 0 ? 'required' : ''
    )

    const esTipoRestriccionUno = computed(() =>
        evento.value?.tipo_invitaciones_abonados === 1
    )

    // El buscador filtra en cliente: `zonas` siempre contiene TODAS las zonas.
    // Filtrar en servidor rompía el guardado, porque el backend reemplaza la
    // configuración completa por la lista que recibe.
    const zonasFiltradas = computed(() => {
        const q = search.value.trim().toLowerCase()
        if (!q) return zonas.value
        return zonas.value.filter(z => (z.name || '').toLowerCase().includes(q))
    })

    // El checkbox de cabecera es un selector "todo o nada" derivado del estado
    // real de las zonas: se muestra marcado solo cuando TODAS tienen taquilla y
    // online activos. Así nunca queda en un modo pegajoso que impida editar
    // zonas sueltas.
    const marcarEnTodos = computed(() =>
        zonas.value.length > 0 &&
        zonas.value.every(z =>
            z.invitaciones_abonado?.taquilla === true &&
            z.invitaciones_abonado?.online === true
        )
    )

    const puedeGuardar = computed(() => {
        if (!evento.value) return false
        const n = evento.value.n_invitaciones_abonados
        if (n === null || n === undefined || n === '') return false
        if (n > 0 && !evento.value.tipo_invitaciones_abonados) return false

        if (evento.value.tipo_invitaciones_abonados === 1) {
            return zonas.value.some(z => {
                const original = zonasOriginales.value.find(o => o.id === z.id)
                if (!original) return false
                return (
                    z.invitaciones_abonado.online !== original.invitaciones_abonado.online ||
                    z.invitaciones_abonado.taquilla !== original.invitaciones_abonado.taquilla
                )
            })
        }

        return true
    })

    // ==================== MÉTODOS ====================
    function syncChange(event, field, zona) {
        const value = event.target.checked

        if (!zona.invitaciones_abonado) {
            zona.invitaciones_abonado = { online: false, taquilla: false }
        }
        zona.invitaciones_abonado[field] = value
    }

    // Marca o desmarca taquilla y online en todas las zonas de una vez.
    function marcarTodasLasZonas(value) {
        zonas.value.forEach(z => {
            if (!z.invitaciones_abonado) {
                z.invitaciones_abonado = { online: false, taquilla: false }
            }
            z.invitaciones_abonado.taquilla = value
            z.invitaciones_abonado.online = value
        })
    }

    function resetData() {
        errors.value = []
        if (evento.value?.tipo_invitaciones_abonados !== 1) {
            zonas.value = zonasOriginales.value.map(z => ({ ...z }))
        }
    }

    async function getDetalleEvento() {
        loading.value = true
        try {
            const params = {
                filters: {},
                evento_id: eventoId
            }
            const res = await store.dispatch(config.getVuexAlias('back.events.detalle-invitaciones-abonados'), params)
            evento.value = res.data
            evento.value.tipo_invitaciones_abonados = evento.value.tipo_invitaciones_abonados != null
                ? Number(evento.value.tipo_invitaciones_abonados)
                : null
            if (evento.value.tipo_invitaciones_abonados === 1) {
                getZonas()
            }
        } catch {
            // Silently fail
        } finally {
            loading.value = false
        }
    }

    async function getZonas() {
        loadingZonas.value = true
        try {
            const params = {
                filters: {},
                evento_id: eventoId
            }
            const res = await store.dispatch(config.getVuexAlias('back.events.zonas-invitaciones-abonados'), params)
            zonas.value = (res.data.data || []).map(z => ({
                ...z,
                invitaciones_abonado: {
                    online: !!(z.invitaciones_abonado && z.invitaciones_abonado.online),
                    taquilla: !!(z.invitaciones_abonado && z.invitaciones_abonado.taquilla)
                }
            }))

            zonasOriginales.value = zonas.value.map(z => ({
                ...z,
                invitaciones_abonado: { ...z.invitaciones_abonado }
            }))
        } catch {
            // Silently fail
        } finally {
            loadingZonas.value = false
        }
    }

    async function guardarConfiguracion() {
        loading.value = true

        try {
            // El backend REEMPLAZA la configuración completa con la lista
            // recibida: hay que enviar TODAS las zonas, no solo las cambiadas,
            // o las omitidas se quedan a 0 al recargar.
            const configuracion = zonas.value.map(z => ({
                zona_id: z.id,
                online: z.invitaciones_abonado.online === true,
                taquilla: z.invitaciones_abonado.taquilla === true
            }))

            const params = {
                evento_id: eventoId,
                n_invitaciones_abonados: evento.value.n_invitaciones_abonados,
                tipo_invitaciones_abonados: evento.value.tipo_invitaciones_abonados ?? null,
                configuracion
            }

            const res = await store.dispatch(config.getVuexAlias('back.events.configuracion-invitaciones-abonados'), params)
            toast.success(res.data?.message)
            zonasOriginales.value = zonas.value.map(z => ({
                ...z,
                invitaciones_abonado: { ...z.invitaciones_abonado }
            }))
        } catch (error) {
            errors.value = []
            if (error.response?.data?.errors?.configuracion) {
                errors.value = [...error.response.data.errors.configuracion]
            }
            toast.error(error.response?.data?.message ?? 'Ha ocurrido un error inesperado')
        } finally {
            loading.value = false
        }
    }

    async function submit(observerRef) {
        const isValid = await observerRef.validate()
        if (isValid) {
            guardarConfiguracion()
        }
    }

    // ==================== WATCHERS ====================
    watch(() => evento.value?.tipo_invitaciones_abonados, newVal => {
        if (newVal === 1) getZonas()
    })

    // ==================== LIFECYCLE ====================
    onMounted(() => {
        if (!teatro.value?.invitaciones_para_abonados) {
            router.go(-1)
            return
        }
        getDetalleEvento()
    })

    return {
        // Estado
        zonas,
        search,
        errors,
        evento,
        loading,
        loadingZonas,
        marcarEnTodos,
        opcionesTipoInvitacionesAbonados,
        // Computed
        esMovil,
        reglasRestriccion,
        esTipoRestriccionUno,
        zonasFiltradas,
        puedeGuardar,
        // Métodos
        syncChange,
        marcarTodasLasZonas,
        resetData,
        getDetalleEvento,
        getZonas,
        guardarConfiguracion,
        submit
    }
}
